import { z } from "zod";
import { parserProfileIdForImportSource } from "../config";

const decimalLike = z.union([z.string(), z.number()]);

const SummaryAmountFieldsSchema = z.object({
  taxCode: z.string().nullish(),
  grossPay: decimalLike.nullish(),
  grossTaxable: decimalLike.nullish(),
  paye: decimalLike.nullish(),
  netPay: decimalLike.nullish(),
  ksErGross: decimalLike.nullish(),
  ksErEsct: decimalLike.nullish(),
  ksEe: decimalLike.nullish(),
  otherSuperEe: decimalLike.nullish(),
  otherSuperEr: decimalLike.nullish(),
  studentLoan: decimalLike.nullish(),
});

export const SummaryPreviewRowSchema = SummaryAmountFieldsSchema.extend({
  employeeCode: z.string().min(1),
  blockIndex: z.number().int().nonnegative().nullish(),
});

export const SummaryPreviewTotalsSchema = SummaryAmountFieldsSchema;

export const SummaryPreviewSchema = z.object({
  rows: z.array(SummaryPreviewRowSchema).default([]),
  totals: SummaryPreviewTotalsSchema.nullish(),
});

export const RunIssueViewSchema = z.object({
  severity: z.enum(["blocking", "warning"]),
  code: z.string(),
  message: z.string(),
  employeeCode: z.string().nullish(),
  blockIndex: z.number().int().nonnegative().nullish(),
  sourcePage: z.number().int().positive().nullish(),
});

export const NormalisationRunArtifactSchema = z.enum([
  "detail.csv",
  "summary.csv",
  "issues.csv",
  "super.csv",
  "bundle.zip",
]);

export const ClientPayNormalisationRunSchema = z.object({
  runId: z.string(),
  status: z.enum(["success", "blocked"]),
  importSource: z.string(),
  parserProfileId: z.string(),
  originalFilename: z.string(),
  payPeriodLabel: z.string().nullish(),
  payslipBlockCount: z.number().int().nonnegative(),
  warningCount: z.number().int().nonnegative().default(0),
  blockingIssueCount: z.number().int().nonnegative().default(0),
  summaryPreview: SummaryPreviewSchema.nullish(),
  issues: z.array(RunIssueViewSchema).default([]),
  availableArtifacts: z.array(NormalisationRunArtifactSchema).default([]),
  createdAt: z.string(),
});

export const RunAcceptedSchema = z.object({
  runId: z.string(),
  status: z.enum(["success", "blocked"]),
  run: ClientPayNormalisationRunSchema,
});

/** FastAPI may serialize Pydantic aliases (camelCase) or field names (snake_case). */
export const ImportSourceViewSchema = z
  .object({
    id: z.string(),
    displayName: z.string().optional(),
    display_name: z.string().optional(),
    parserProfileId: z.string().optional(),
    parser_profile_id: z.string().optional(),
    enabled: z.boolean(),
  })
  .transform((row) => {
    const parserProfileId =
      row.parserProfileId ??
      row.parser_profile_id ??
      parserProfileIdForImportSource(row.id);
    if (parserProfileId === null) {
      throw new Error(`Import source "${row.id}" is missing parserProfileId`);
    }
    return {
      id: row.id,
      displayName: row.displayName ?? row.display_name ?? row.id,
      parserProfileId,
      enabled: row.enabled,
    };
  });

function normaliseImportSourceListPayload(value: unknown): unknown {
  if (value === null || typeof value !== "object") {
    return value;
  }
  const record = value as Record<string, unknown>;
  if (record.importSources !== undefined || record.import_sources !== undefined) {
    return {
      importSources: record.importSources ?? record.import_sources,
    };
  }
  return value;
}

export const ImportSourceListResultSchema = z.preprocess(
  normaliseImportSourceListPayload,
  z.object({
    importSources: z.array(ImportSourceViewSchema),
  }),
);

export type SummaryPreviewRowWire = z.infer<typeof SummaryPreviewRowSchema>;
export type SummaryPreviewTotalsWire = z.infer<typeof SummaryPreviewTotalsSchema>;
export type ClientPayNormalisationRunWire = z.infer<
  typeof ClientPayNormalisationRunSchema
>;
export type RunAcceptedWire = z.infer<typeof RunAcceptedSchema>;
